import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  INTERCEPTForbidden,
  INTERCEPTServiceUnavailable,
} from 'app/core/interceptors/contexts';
import { ServiceBase } from 'app/core/services/service.base';
import {
  RemoteSupportConnectRefusal,
  RemoteSupportGrant,
} from 'app/shared/models/remote-support.model';

/** Why a code did not open a session, as the page explains it (`remote-support.errors.*`). */
export type RemoteSupportConnectFailure =
  | 'code-not-recognised'
  | 'not-permitted'
  | 'session-owned'
  | 'rate-limited'
  | 'invalid-code'
  | 'unavailable';

const REFUSAL_FAILURES: Record<
  RemoteSupportConnectRefusal,
  RemoteSupportConnectFailure
> = {
  CODE_NOT_RECOGNISED: 'code-not-recognised',
  NOT_PERMITTED: 'not-permitted',
  SESSION_OWNED: 'session-owned',
};

/**
 * Maps a refused `remote-access-connect` to what the page tells the technician. A 403 names its
 * reason in the body; anything the contract does not name is reported as unavailable.
 */
export function connectFailureOf(error: unknown): RemoteSupportConnectFailure {
  if (!(error instanceof HttpErrorResponse)) {
    return 'unavailable';
  }
  if (error.status === HttpStatusCode.TooManyRequests) {
    return 'rate-limited';
  }
  if (error.status === HttpStatusCode.BadRequest) {
    return 'invalid-code';
  }
  const reason = error.error?.reason as RemoteSupportConnectRefusal | undefined;
  if (
    error.status === HttpStatusCode.Forbidden &&
    reason &&
    REFUSAL_FAILURES[reason]
  ) {
    return REFUSAL_FAILURES[reason];
  }
  return 'unavailable';
}

/**
 * Technician routes of remote support on the isoFarm gateway. The page explains refusals itself,
 * so 403 and 503 skip the global interceptor; 401 still signs the user out.
 */
@Injectable({
  providedIn: 'root',
})
export class RemoteSupportService extends ServiceBase {
  private readonly http = inject(HttpClient);

  /** Redeems the code the operator read from the terminal for a signalling ticket. */
  connect(code: string): Observable<RemoteSupportGrant> {
    return this.http
      .post<RemoteSupportGrant>(
        `${this.UrlServiceV1}remote-access-connect`,
        { code },
        this.options(),
      )
      .pipe(catchError(this.serviceError));
  }

  private options() {
    return {
      ...this.obterAuthHeader(),
      context: new HttpContext()
        .set(INTERCEPTForbidden, false)
        .set(INTERCEPTServiceUnavailable, false),
    };
  }
}
